// Local presentation of the authoritative hex route. No game-state mutation.
export interface Point { x: number; z: number }
export interface Walker extends Point { id: number; vx: number; vz: number; radius: number }
export interface Obstacle extends Point { radius: number }
const TAU = Math.PI * 2
const length = (p: Point): number => Math.hypot(p.x, p.z)

/** Snapshot positions, not partially updated entity positions, feed every walker.
 * Look ahead to the closest approach and pass on the right before bodies touch. */
export function separation(me: Walker, neighbors: Iterable<Walker>): Point {
  let x = 0, z = 0
  for (const other of neighbors) {
    if (other.id === me.id) continue
    const dx = me.x - other.x, dz = me.z - other.z
    const distance = Math.hypot(dx, dz), clearance = me.radius + other.radius + 1
    const vx = me.vx - other.vx, vz = me.vz - other.vz, vv = vx * vx + vz * vz
    const t = vv > 0.01 ? Math.max(0, Math.min(0.8, -(dx * vx + dz * vz) / vv)) : 0
    const future = Math.hypot(dx + vx * t, dz + vz * t)
    if (distance < clearance) {
      // A deterministic, antisymmetric direction also separates exact overlaps.
      const angle = ((Math.min(me.id, other.id) * 2.399963) % TAU)
      const sign = me.id < other.id ? 1 : -1
      const nx = distance > 0.001 ? dx / distance : Math.cos(angle) * sign
      const nz = distance > 0.001 ? dz / distance : Math.sin(angle) * sign
      x += nx * (clearance - distance); z += nz * (clearance - distance)
    }
    if (t > 0 && future < clearance && vv > 0.01) {
      const speed = Math.sqrt(vv)
      const weight = (clearance - future) * (1 - t / 1.6)
      // The relative heading gives opposing walkers opposite passing lanes.
      x += -vz / speed * weight; z += vx / speed * weight
    }
  }
  const size = Math.hypot(x, z), cap = 18
  return size > cap ? { x: x * cap / size, z: z * cap / size } : { x, z }
}

function outside(p: Point, c: Obstacle, fallback: Point): Point {
  const dx = p.x - c.x, dz = p.z - c.z, d = Math.hypot(dx, dz)
  if (d >= c.radius) return p
  const f = d > 0.001 ? { x: dx / d, z: dz / d } : fallback
  return { x: c.x + f.x * c.radius, z: c.z + f.z * c.radius }
}
function direct(a: Point, b: Point, distance: number): Point {
  const dx = b.x - a.x, dz = b.z - a.z, d = Math.hypot(dx, dz)
  const t = d > 0 ? Math.min(1, distance / d) : 0
  return { x: a.x + dx * t, z: a.z + dz * t }
}
export function segmentClear(a: Point, b: Point, c: Obstacle): boolean {
  const dx = b.x - a.x, dz = b.z - a.z, dd = dx * dx + dz * dz
  const t = dd > 0 ? Math.max(0, Math.min(1, ((c.x - a.x) * dx + (c.z - a.z) * dz) / dd)) : 0
  return Math.hypot(a.x + dx * t - c.x, a.z + dz * t - c.z) >= c.radius - 0.001
}

/** Follow tangents and an arc, rather than projecting a crossing onto opposite
 * sides of a wall. The distance budget prevents a centre-crossing teleport. */
export function aroundCircle(start: Point, goal: Point, distance: number, c: Obstacle): Point {
  const a = outside(start, c, { x: -1, z: 0 })
  const ad = { x: a.x - c.x, z: a.z - c.z }, da = length(ad)
  const b = outside(goal, c, { x: ad.x / da, z: ad.z / da })
  if (segmentClear(a, b, c)) return direct(a, b, distance)
  const db = Math.hypot(b.x - c.x, b.z - c.z)
  const alpha = Math.atan2(ad.z, ad.x), beta = Math.atan2(b.z - c.z, b.x - c.x)
  const ta = Math.acos(Math.min(1, c.radius / da)), tb = Math.acos(Math.min(1, c.radius / db))
  const entry = Math.sqrt(Math.max(0, da * da - c.radius * c.radius))
  const exit = Math.sqrt(Math.max(0, db * db - c.radius * c.radius))
  let best = { sign: 1, angle: 0, arc: 0, total: Infinity }
  for (const sign of [1, -1]) {
    const angle = alpha + sign * ta, end = beta - sign * tb
    const arc = ((sign * (end - angle)) % TAU + TAU) % TAU
    const total = entry + arc * c.radius + exit
    if (total < best.total - 0.001) best = { sign, angle, arc, total }
  }
  const at = (angle: number): Point => ({ x: c.x + Math.cos(angle) * c.radius, z: c.z + Math.sin(angle) * c.radius })
  if (distance <= entry) return direct(a, at(best.angle), distance)
  const remaining = distance - entry
  if (remaining <= best.arc * c.radius) return at(best.angle + best.sign * remaining / c.radius)
  return direct(at(best.angle + best.sign * best.arc), b, remaining - best.arc * c.radius)
}

/** Bounded local wall sliding. If two obstacles leave no clearance, wait rather
 * than teleport through either. The server can still choose a different hex. */
export function avoidObstacles(start: Point, goal: Point, distance: number, obstacles: readonly Obstacle[]): Point {
  let a = start
  // Newly visible units / newly raised walls can initially contain a body.
  for (let pass = 0; pass < 3; pass++) for (const c of obstacles) a = outside(a, c, { x: -1, z: 0 })
  let result = direct(a, goal, distance)
  for (let pass = 0; pass < 3; pass++) {
    const hit = obstacles.find(c => !segmentClear(a, result, c))
    if (!hit) return result
    result = aroundCircle(a, goal, distance, hit)
    // An arc's chord is slightly inside the circle; its endpoint is the rendered
    // pose. Check other walls, and use tiny arc steps (normal frame distance).
    if (obstacles.every(c => Math.hypot(result.x - c.x, result.z - c.z) >= c.radius - 0.01)) return result
  }
  return a
}
